import { createContext, useContext, useEffect, useState, ReactNode } from "react";
import type { Session, User } from "@supabase/supabase-js";
import { supabase } from "@/lib/supabase";
import { EXTENSION_IDS } from "@/lib/extension-config";
import { toast } from "sonner";

declare const chrome: any;

interface AuthContextType {
  user: User | null;
  session: Session | null;
  loading: boolean;
  signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType>({
  user: null,
  session: null,
  loading: true,
  signOut: async () => {},
});

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Get the initial session
    supabase.auth.getSession().then(({ data }) => {
      setSession(data.session);
      setLoading(false);
    });

    // Listen for auth state changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      setSession(session);
      setLoading(false);

      if (event === "SIGNED_IN" && session?.user) {
        const user = session.user;
        const metadata = user.user_metadata || {};
        
        // Trigger Brevo signup if user was created within the last 30 seconds and not subscribed yet
        const isNewUser = new Date(user.created_at).getTime() > Date.now() - 30000;
        const alreadySubscribed = localStorage.getItem('brevo_subscribed_' + user.id);
        if (isNewUser && !alreadySubscribed) {
          console.log('[Alvaru Auth] New user signup detected. Subscribing to Brevo:', user.email);
          localStorage.setItem('brevo_subscribed_' + user.id, 'true');
          
          supabase.functions.invoke('subscribe-brevo', {
            body: { email: user.email, name: (metadata.full_name || user.email?.split('@')[0] || '') }
          }).then(({ data, error }) => {
            console.log('[Alvaru Auth] subscribe-brevo response:', { data, error });
          }).catch(err => {
            console.error('[Alvaru Auth] subscribe-brevo failed:', err);
          });
        }

        // Send data to extension if available
        console.log('[Alvaru Auth] SIGNED_IN event — attempting to sync with extension IDs:', EXTENSION_IDS);
        
        if (typeof chrome !== 'undefined' && chrome.runtime?.sendMessage) {
          EXTENSION_IDS.forEach(id => {
            // PING test
            console.log(`[Alvaru Auth] Sending PING to extension ${id}...`);
            chrome.runtime.sendMessage(id, { action: 'PING' }, (response: any) => {
              if (chrome.runtime.lastError) {
                console.log(`[Alvaru Auth] PING error for ${id}:`, chrome.runtime.lastError.message);
              } else {
                console.log(`[Alvaru Auth] PING response from ${id}:`, response);
              }
            });

            // Login sync
            try {
              chrome.runtime.sendMessage(id, {
                action: 'ALVARU_LOGIN',
                email: user.email,
                token: session.access_token,
                user: session.user,
                firstName: metadata.first_name || metadata.firstName || "",
                lastName: metadata.last_name || metadata.lastName || "",
                phone: metadata.phone || user.phone || ""
              }, (response) => {
                if (chrome.runtime.lastError) {
                  console.warn(`[Alvaru Auth] Extension ${id} sendMessage error (SIGNED_IN):`, chrome.runtime.lastError.message);
                } else {
                  console.log(`[Alvaru Auth] Extension ${id} responded to ALVARU_LOGIN (SIGNED_IN):`, response);
                }
              });
            } catch (err) {
              console.warn(`[Alvaru Auth] Could not communicate with extension ${id} (SIGNED_IN):`, err);
            }
          });
        } else {
          console.warn('[Alvaru Auth] chrome.runtime.sendMessage not available');
        }
      }
    });

    return () => subscription.unsubscribe();
  }, []);

  // NEW: Persistent check to send login status to extension whenever session changes
  useEffect(() => {
    if (session?.user) {
      const user = session.user;
      const metadata = user.user_metadata || {};
      
      console.log('[Alvaru Auth] Session effect — attempting to sync with extension IDs:', EXTENSION_IDS);
      
      if (typeof chrome !== 'undefined' && chrome.runtime?.sendMessage) {
        EXTENSION_IDS.forEach(id => {
          try {
            chrome.runtime.sendMessage(id, {
              action: 'ALVARU_LOGIN',
              email: user.email,
              token: session.access_token,
              user: session.user,
              firstName: metadata.first_name || metadata.firstName || "",
              lastName: metadata.last_name || metadata.lastName || "",
              phone: metadata.phone || user.phone || ""
            }, (response: any) => {
              if (chrome.runtime.lastError) {
                // Silently handle expected errors for inactive extensions
                console.debug(`[Alvaru Auth] Extension ${id} not reachable:`, chrome.runtime.lastError.message);
              } else if (response) {
                console.log(`[Alvaru Auth] Extension ${id} responded to ALVARU_LOGIN:`, response);
              }
            });
          } catch (err) {
            // Catch synchronous errors in sendMessage call
            console.debug(`[Alvaru Auth] Synchronous error messaging extension ${id}:`, err);
          }
        });
      }
    }
  }, [session, loading]);

  const signOut = async () => {
    console.log('[Alvaru Auth] signOut called');
    try {
      const { error } = await supabase.auth.signOut();
      if (error) {
        console.error('[Alvaru Auth] Error during Supabase signOut:', error);
      }
      
      setSession(null); // Force local state update
      
      // Notify extension
      if (typeof chrome !== 'undefined' && chrome.runtime?.sendMessage) {
        EXTENSION_IDS.forEach(id => {
          console.log(`[Alvaru Auth] Notifying extension ${id} of logout`);
          chrome.runtime.sendMessage(id, { action: 'ALVARU_LOGOUT' }).catch(() => {});
        });
      }
      
      toast.success("Signed out successfully");
    } catch (err) {
      console.error('[Alvaru Auth] Exception during signOut:', err);
    }
  };

  return (
    <AuthContext.Provider value={{ user: session?.user ?? null, session, loading, signOut }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
